import {
  Feature,
  OrderStatus as OrderStatusEnum,
  StructureStatus,
} from "@enums";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PlanningTemplateBottomBar } from "@pages/OrderTracker/components";
import LoadingRTStructTPS from "@pages/OrderTracker/components/PlanningAutomation/LoadingRTStructTPS";
import IsocenterPanel from "@pages/OrderTracker/pages/Isocenter/IsocenterPanel";
import RTStructTable from "@pages/OrderTracker/pages/RTStructTable";
import {
  IsocenterOptionOut,
  OrderStatus,
  RtStructOut,
  useAutomationIntegrationApiV1ListDensityOverridesQuery,
  useAutomationIntegrationApiV1ListMachinesQuery,
  useAutomationIntegrationApiV1ListTemplateDetailsQuery,
  useAutomationIntegrationApiV1ListTemplatesQuery,
  useAutomationIntegrationApiV1RequestPlanGenerationMutation,
  useWebApiV1GetFeatureSwitchesForOrderQuery,
  useWebApiV1GetRtStructsQuery,
  useWebApiV1OrderQuery,
  useWebApiV1RelatedPrescriptionsQuery,
  useWebApiV1UpdateOrderMutation,
} from "@providers/hop-ord-server/api";
import TemplateSelectionTable from "../components/PlanningAutomation/TemplateSelectionTable";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { toLocaleDateTimeString } from "@utils";
import useRefreshImportedData from "@utils/useRefreshImportedData";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StructureMapping, {
  DensityOverrideMapping,
  StructureMap,
} from "../components/PlanningAutomation/StructureMapping";
import MachineAndBeamSelectboxes from "./MachineAndBeamSelectboxes";
import RefreshRtStructsButton from "./RefreshRtStructsButton";

interface Props {
  orderId: number;
  disablePlanGeneration: boolean;
}

interface StructureObj {
  name: string;
  type: string;
}

const PlanningTemplateApi = ({
  orderId,
  disablePlanGeneration,
}: Props): JSX.Element => {
  const navigate = useNavigate();

  const [structureState, setStructureState] = useState<StructureMap[]>([]);
  const [densityOverrideState, setDensityOverrideState] = useState<
    DensityOverrideMapping[]
  >([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [relatedPrescription, setRelatedPrescription] = useState("");
  const [relatedPrescriptionId, setRelatedPrescriptionId] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedBeamEnergy, setSelectedBeamEnergy] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showBeamEnergyError, setShowBeamEnergyError] = useState(false);
  const [showDensityOverrideError, setShowDensityOverrideError] =
    useState(false);
  const [selectedRtStructId, setSelectedRtStructId] = useState<number>(-1);
  const [selectedIsocenter, setSelectedIsocenter] =
    useState<IsocenterOptionOut>({
      name: "",
      type: "",
      optionLabel: "",
      category: "",
    });
  const {
    handleRefresh: handleRefreshImportedData,
    lastRefreshedAt,
    refreshStatus,
    setRefreshStatus,
    structuresRetrieved,
  } = useRefreshImportedData(orderId, selectedRtStructId, selectedTemplate);

  const [updateOrder] = useWebApiV1UpdateOrderMutation();
  const { data: rtStructsQueryData, isLoading: rtStructsQueryLoading } =
    useWebApiV1GetRtStructsQuery({ orderId });
  const [rtStructs, setRtStructs] = useState<RtStructOut[]>([]);

  const { data: order, isLoading: orderLoading } = useWebApiV1OrderQuery(
    {
      orderId,
    },
    { refetchOnMountOrArgChange: true },
  );

  const tenantIdParams =
    order?.tenantId !== undefined && order?.tenantId !== null
      ? { tenantId: order.tenantId }
      : skipToken;

  const { data: featureSwitchData } =
    useWebApiV1GetFeatureSwitchesForOrderQuery({ orderId });

  const multipleRtStructuresFeatureEnabled = useMemo(
    () =>
      featureSwitchData?.find(
        (feature) => feature.name === Feature.MULTIPLE_RT_STRUCTURES,
      ),
    [featureSwitchData],
  );

  const selectIsocenterFeatureEnabled = useMemo(
    () =>
      featureSwitchData?.find(
        (feature) => feature.name === Feature.SELECT_ISOCENTER,
      ),
    [featureSwitchData],
  );

  const { data: machines, isLoading: machinesLoading } =
    useAutomationIntegrationApiV1ListMachinesQuery(tenantIdParams);

  const { data: densityOverrides, isLoading: densityOverridesLoading } =
    useAutomationIntegrationApiV1ListDensityOverridesQuery(tenantIdParams);

  const { data: templateNames, isLoading: templateNamesLoading } =
    useAutomationIntegrationApiV1ListTemplatesQuery(tenantIdParams);

  // Site query (to get names and ID's for related prescriptions)
  const { data: relatedPrescriptions, isLoading: relatedPrescriptionsLoading } =
    useWebApiV1RelatedPrescriptionsQuery({
      orderId: orderId.toString(),
    });

  const { data: template } =
    useAutomationIntegrationApiV1ListTemplateDetailsQuery(
      selectedTemplate && order
        ? { tenantId: order.tenantId, templateName: selectedTemplate }
        : skipToken,
    );

  const [sendPlan] =
    useAutomationIntegrationApiV1RequestPlanGenerationMutation();

  const refreshingImportedData = refreshStatus !== "idle";
  const rtStructExists = order?.rtstructStatus === StructureStatus.COMPLETE;
  const ctExists = order?.ctStatus === StructureStatus.COMPLETE;
  const showAutomation = rtStructExists && ctExists;
  const showCancelButton = order?.status === OrderStatusEnum.SENT_TO_AUTOMATION;

  // Get beam energies for selected machine
  const beamEnergyOptions = useMemo(
    () =>
      machines?.find((m) => m.name === selectedMachine)?.beam_energies || [],
    [machines, selectedMachine],
  );

  // Temp options while the list data is not available
  const machineOptions = useMemo(
    () => machines?.map((data) => data.name) || [],
    [machines],
  );

  const importedDataLastRefreshed = useMemo(() => {
    const datetime = lastRefreshedAt || order?.lastRefreshedImportedDataAt;
    return datetime ? toLocaleDateTimeString(datetime) : "";
  }, [lastRefreshedAt, order]);

  const relatedPrescriptionOptions = useMemo(
    () =>
      relatedPrescriptions?.map((prescription) => ({
        label: prescription.relatedPrescriptionName,
        id: prescription.relatedPrescriptionId,
      })),
    [relatedPrescriptions],
  );

  const disablePlanGenerationButton =
    disablePlanGeneration ||
    !selectedTemplate ||
    !relatedPrescription ||
    !selectedMachine ||
    !selectedBeamEnergy;

  useEffect(() => {
    // Default to the first machine if there is only one
    if (machines?.length === 1 && !selectedMachine) {
      setSelectedMachine(machines[0].name);
    }
  }, [machines]);

  // Auto-select beam energy if only one option is available
  useEffect(() => {
    if (beamEnergyOptions.length === 1 && !selectedBeamEnergy) {
      setSelectedBeamEnergy(beamEnergyOptions[0]);
    }
  }, [beamEnergyOptions, selectedBeamEnergy]);

  useEffect(() => {
    if (rtStructsQueryData) {
      setRtStructs(rtStructsQueryData);
      setSelectedRtStructId(rtStructsQueryData?.[0]?.id || -1);
    }
  }, [orderId, rtStructsQueryData]);

  useEffect(() => {
    if (relatedPrescriptions?.length === 1 && !relatedPrescription) {
      // Automatically select the first related prescription and id if there is only one
      setRelatedPrescription(relatedPrescriptions[0].relatedPrescriptionName);
      setRelatedPrescriptionId(
        relatedPrescriptions[0].relatedPrescriptionId.toString(),
      );
    }
  }, [relatedPrescriptions]);

  const setOrderStatus = (status: OrderStatus) => {
    return updateOrder({
      orderId,
      orderIn: {
        status,
      },
    });
  };

  const scrollToFirstInvalidField = (structures: StructureMap[]) => {
    if (!selectedMachine) {
      const machineElement = document.getElementById("selected-machine");
      machineElement?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    } else {
      const firstInvalidStructure = structures.find((s) => !s.isValid);
      const firstInvalidElement = document.getElementById(
        `planning-template-value-${firstInvalidStructure?.id}`,
      );
      firstInvalidElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };

  const runValidation = (structures: StructureMap[]) => {
    const allValid = structures.every((s) => {
      return !(s.mandatory && !s.values.length);
    });

    const updatedStructures = structures.map((structure) => {
      const isInvalid = structure.mandatory && !structure.values.length;
      return {
        ...structure,
        isValid: !isInvalid,
      };
    });

    setStructureState(updatedStructures);
    return (
      allValid &&
      !!selectedMachine &&
      !!selectedBeamEnergy &&
      !!selectedTemplate &&
      !!relatedPrescription
    );
  };

  const submitPlanGeneration = async () => {
    setHasSubmitted(true);
    setShowBeamEnergyError(!!selectedMachine);

    // Remove empty density override mappings (both fields empty)
    const nonEmptyDensityOverrides = densityOverrideState.filter(
      (item) => item.structure || item.material,
    );
    setDensityOverrideState(nonEmptyDensityOverrides);

    // Check for incomplete density override mappings (one field filled, one empty)
    const hasIncompleteDensityMapping = nonEmptyDensityOverrides.some(
      (item) =>
        (item.structure && !item.material) ||
        (!item.structure && item.material),
    );
    setShowDensityOverrideError(hasIncompleteDensityMapping);
    if (hasIncompleteDensityMapping) {
      return;
    }

    if (!runValidation(structureState)) {
      scrollToFirstInvalidField(structureState);
      return;
    }

    // Submit has been clicked and / or validation has run successfully. Submit form.
    const structureMap: StructureObj[] = [];
    structureState.forEach((s) => {
      if (s.values) {
        const map = s.values.map((value) => ({
          name: s.name,
          type: value,
        }));
        structureMap.push(...map);
      }
    });

    // Build density override map
    const densityMap = nonEmptyDensityOverrides
      .filter((override) => override.material && override.structure)
      .map((override) => ({
        material: override.material,
        structure: override.structure,
      }));

    let isocenter = null;

    if (selectIsocenterFeatureEnabled) {
      if (selectedIsocenter.name && selectedIsocenter.type) {
        isocenter = {
          name: selectedIsocenter.name,
          type: selectedIsocenter.type,
        };
      }
    }

    sendPlan({
      orderId,
      planGenerationAttemptIn: {
        template: selectedTemplate,
        structures: structureMap,
        machine: selectedMachine,
        beam_energy: selectedBeamEnergy,
        related_prescription: relatedPrescription,
        related_prescription_id: relatedPrescriptionId,
        density_overrides: densityMap,
        rt_struct_id: selectedRtStructId,
        isocentre: isocenter,
      },
    }).then(() => {
      setHasSubmitted(true);

      // Reset the form state
      setSelectedTemplate("");
      setRelatedPrescription("");
      setRelatedPrescriptionId("");
      setSelectedMachine("");
      setSelectedBeamEnergy("");
      setDensityOverrideState([]);
      setStructureState([]);
      setShowBeamEnergyError(false);
      setShowDensityOverrideError(false);

      setOrderStatus("sent_to_automation");
      navigate("automated");
    });
  };

  const handleRTStructSelect = (rtStructId: number) => {
    if (rtStructId === selectedRtStructId) {
      return;
    }

    setSelectedRtStructId(rtStructId);

    if (selectedTemplate && !refreshingImportedData) {
      setRefreshStatus("refetching order");
    }
  };

  const changeRTStructOrdering = async (ordering: "asc" | "desc") => {
    // Sort by ascending series datetime if ordering is ascending, otherwise sort by descending series datetime using Dayjs
    const orderedRtStructs = [...rtStructs].sort((first, second) => {
      if (ordering === "asc") {
        return dayjs(first.seriesDatetime).diff(dayjs(second.seriesDatetime));
      } else {
        return dayjs(second.seriesDatetime).diff(dayjs(first.seriesDatetime));
      }
    });
    setRtStructs(orderedRtStructs);
    setSelectedRtStructId(orderedRtStructs?.[0]?.id || -1);

    if (selectedTemplate && !refreshingImportedData) {
      setRefreshStatus("refetching order");
    }
  };

  if (
    orderLoading ||
    templateNamesLoading ||
    machinesLoading ||
    relatedPrescriptionsLoading ||
    densityOverridesLoading
  ) {
    return (
      <PlanningTemplateBottomBar
        orderId={orderId}
        showCancelButton={showCancelButton}
        onSubmitPlanGeneration={submitPlanGeneration}
        disablePlanGeneration={disablePlanGenerationButton}
      />
    );
  }

  return (
    <Stack
      sx={{
        width: 1,
        height: 1,
        marginBottom: 3,
        gap: 2,
      }}
    >
      {!showAutomation && (
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography>
            Missing RT structure set. Not ready for planning.
          </Typography>
          <RefreshRtStructsButton
            refreshingImportedData={refreshingImportedData}
            handleRefreshImportedData={handleRefreshImportedData}
          />
        </Stack>
      )}
      {showAutomation && (
        <Stack
          sx={{
            gap: 2,
            width: 1,
          }}
        >
          {!multipleRtStructuresFeatureEnabled && (
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <RefreshRtStructsButton
                refreshingImportedData={refreshingImportedData}
                handleRefreshImportedData={handleRefreshImportedData}
              />
              {importedDataLastRefreshed && (
                <Typography
                  variant="caption"
                  data-testid="last-refreshed-timestamp"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Last refreshed: {importedDataLastRefreshed}
                </Typography>
              )}
            </Stack>
          )}
          <FormControl
            sx={{ paddingTop: 1, width: "50%" }}
            error={hasSubmitted && !relatedPrescription}
          >
            <Autocomplete
              disableClearable
              options={relatedPrescriptionOptions || []}
              value={{ label: relatedPrescription, id: relatedPrescriptionId }}
              fullWidth
              onChange={(_, newValue: { label: string; id: string } | null) => {
                setRelatedPrescription(newValue?.label || "");
                setRelatedPrescriptionId(newValue?.id || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={hasSubmitted && !relatedPrescription}
                  label="Related Site *"
                />
              )}
            />
            {hasSubmitted && !relatedPrescription && (
              <FormHelperText>This field is required</FormHelperText>
            )}
          </FormControl>
          {multipleRtStructuresFeatureEnabled && (
            <Card
              data-testid="planning-template-rtstruct-table"
              variant="outlined"
            >
              <CardContent>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">
                      RT structure sets
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      Select the appropriate RT structure sets for treatment
                      planning.
                    </Typography>
                  </Box>
                  <Stack
                    direction="column"
                    sx={{
                      gap: 1,
                    }}
                  >
                    <RefreshRtStructsButton
                      refreshingImportedData={refreshingImportedData}
                      handleRefreshImportedData={handleRefreshImportedData}
                    />
                  </Stack>
                </Stack>
                {rtStructsQueryLoading ||
                refreshStatus === "awaiting refresh" ||
                refreshStatus === "requesting refresh" ? (
                  <LoadingRTStructTPS />
                ) : (
                  <RTStructTable
                    data={rtStructs ?? []}
                    onSelect={handleRTStructSelect}
                    selectedId={selectedRtStructId}
                    changeOrdering={changeRTStructOrdering}
                  />
                )}
              </CardContent>
            </Card>
          )}
          <Typography variant="h6">Template selection</Typography>
          <TemplateSelectionTable
            templates={templateNames ?? []}
            selectedTemplate={selectedTemplate}
            error={hasSubmitted && !selectedTemplate}
            onSelect={(templateName) => {
              if (!templateName || templateName === selectedTemplate) return;
              setSelectedTemplate(templateName);
              setDensityOverrideState([]);
              if (!refreshingImportedData) {
                setRefreshStatus("refetching order");
              }
            }}
          />
          {selectedTemplate && (
            <>
              <Typography variant="h6">Plan configuration</Typography>
              <StructureMapping
                template={template}
                availableStructures={
                  refreshingImportedData ? null : structuresRetrieved.structures
                }
                availableMaterials={densityOverrides}
                hasSubmitted={hasSubmitted}
                setHasSubmitted={setHasSubmitted}
                setStructureState={setStructureState}
                structureState={structureState}
                runValidation={runValidation}
                densityOverrideState={densityOverrideState}
                setDensityOverrideState={setDensityOverrideState}
                showDensityOverrideError={showDensityOverrideError}
              />
              {selectIsocenterFeatureEnabled && (
                <IsocenterPanel
                  selectedIsocenter={selectedIsocenter}
                  setSelectedIsocenter={setSelectedIsocenter}
                  templateName={template?.name || ""}
                  selectedRtStructId={selectedRtStructId}
                  isLoading={refreshingImportedData}
                />
              )}
              <MachineAndBeamSelectboxes
                selectedMachine={selectedMachine}
                setSelectedMachine={setSelectedMachine}
                machineOptions={machineOptions}
                beamEnergyOptions={beamEnergyOptions}
                selectedBeamEnergy={selectedBeamEnergy}
                setSelectedBeamEnergy={setSelectedBeamEnergy}
                hasSubmitted={hasSubmitted}
                showBeamEnergyError={showBeamEnergyError}
                setShowBeamEnergyError={setShowBeamEnergyError}
              />
            </>
          )}

          {(!templateNames?.length || !machineOptions?.length) && (
            <Stack
              direction="row"
              sx={{
                gap: 1,
              }}
            >
              <Typography color="error">
                No templates and/or machines found.
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      <PlanningTemplateBottomBar
        orderId={orderId}
        showCancelButton={showCancelButton}
        onSubmitPlanGeneration={submitPlanGeneration}
        disablePlanGeneration={disablePlanGenerationButton}
      />
    </Stack>
  );
};

export default PlanningTemplateApi;
